import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { CreateProjectDialog } from '../../components/projects/create-project-dialog.tsx'
import * as layout from '../../components/projects/project-page-layout.css.ts'
import { RepositoryNames } from '../../components/projects/repository-names.tsx'
import { ScopeHierarchyHint } from '../../components/projects/scope-hierarchy-hint.tsx'
import { isInteractiveElementClick } from '../../components/table/row-interaction.ts'
import { TableBodySkeleton } from '../../components/table/table-body-skeleton.tsx'
import { Routes } from '../../routes.ts'
import { useCurrentUser, useGetProjects, useGetRepositoryCount, useIsAdmin } from '../../utils/api-client/api-hooks.ts'
import type { ProjectResponse } from '../../utils/api-client/api-schemas.ts'
import * as styles from './projects-page.css.ts'

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const { isError: currentUserFailed } = useCurrentUser()
  const { projects, isPending, isError } = useGetProjects()
  const { data: repositoryCount } = useGetRepositoryCount()
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const grouped = useMemo(() => projects.reduce((sum, project) => sum + project.repository_count, 0), [projects])

  return (
    <div className={layout.page}>
      <div className={layout.titleBlock}>
        <h1 className={layout.heading}>Projects</h1>
        <p className={layout.description}>
          Group repositories to scope feedback
          <ScopeHierarchyHint />
        </p>
      </div>

      <div className={layout.bar}>
        <p className={layout.summary}>
          {grouped} of {repositoryCount ?? '—'} <span className={layout.summaryMuted}>repositories grouped</span>
        </p>
        {isAdmin === true && (
          <DefaultButton size="small" onClick={() => setIsCreateOpen(true)}>
            + Create project
          </DefaultButton>
        )}
        {isAdmin === false && <p className={layout.readOnlyNotice}>Only administrators can create projects.</p>}
        {isAdmin === undefined && currentUserFailed && (
          <p className={layout.readOnlyNotice}>Unable to confirm your permissions. Reload the page to try again.</p>
        )}
      </div>

      {/* A failed query is not an empty estate: "No projects yet" would tell the reader their projects
          are gone when the page simply could not read them. */}
      {isError ? (
        <div className={layout.emptyCard}>
          <h3 className={layout.emptyTitle}>Unable to load projects</h3>
          <p className={layout.emptyText}>Something went wrong. Reload the page to try again.</p>
        </div>
      ) : isPending || projects.length > 0 ? (
        <ProjectsTable projects={projects} isPending={isPending} />
      ) : (
        <div className={layout.emptyCard}>
          <h3 className={layout.emptyTitle}>No projects yet</h3>
          <p className={layout.emptyText}>
            Feedback applies to a repository or the whole organization until you create a project.
          </p>
        </div>
      )}

      {isCreateOpen && (
        <CreateProjectDialog
          onClose={() => setIsCreateOpen(false)}
          onCreated={project => {
            setIsCreateOpen(false)
            void navigate(Routes.ProjectDetailsPage.createPath({ projectId: project.id }))
          }}
        />
      )}
    </div>
  )
}

const ProjectsTable: React.FC<{ projects: ProjectResponse[]; isPending: boolean }> = ({ projects, isPending }) => {
  const navigate = useNavigate()

  const openProject = (projectId: string) => void navigate(Routes.ProjectDetailsPage.createPath({ projectId }))

  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <colgroup>
          <col className={styles.projectColumn} />
          <col />
        </colgroup>
        <thead>
          <tr className={styles.headRow}>
            <th className={styles.columnHeader}>Project</th>
            <th className={styles.columnHeader}>Repositories</th>
          </tr>
        </thead>
        {isPending ? (
          <TableBodySkeleton columnCount={2} rowCount={3} rowHeightInPixels={72} label="Loading projects" />
        ) : (
          <tbody>
            {projects.map(project => (
              <tr
                key={project.id}
                className={classNames(styles.row, styles.selectableRow)}
                tabIndex={0}
                onClick={event => {
                  if (!isInteractiveElementClick(event)) {
                    openProject(project.id)
                  }
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    openProject(project.id)
                  }
                }}
                aria-label={`Open ${project.name}`}
              >
                <td className={styles.cell}>
                  <span className={styles.projectName}>{project.name}</span>
                  {project.description && <span className={styles.projectDescription}>{project.description}</span>}
                </td>
                <td className={styles.cell}>
                  <RepositoryNames
                    names={(project._embedded?.repositories._embedded.items ?? []).map(
                      repository => repository.full_name
                    )}
                    total={project.repository_count}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  )
}
